import { useCallback, useRef, useState } from 'react';
import { MAPS_LINK_SOURCE_KEYS, MAPS_LINK_UNREAD_KEYS, Size, readMapsLink, t, type MapsLinkAnswer, type MapsLinkSource } from '@bms2/shared';
import { TextInput } from '../../components/controls';
import { post } from './api';
import styles from './RecordPage.module.css';

/**
 * Paste a Maps link (piece 13; decision 1600): a Google Maps link a customer
 * sent is read for its place, and the form it sits in fills from the answer
 * for the person to read and correct. 🔨 Nothing is saved here; the form's own
 * save writes the point as the pin, with the person who pasted it.
 */
export const MAPS_LINK_PATH = '/maps/link';

/** The API's read of one pasted link, or null where the read itself failed. */
export async function readMapsLinkAnswer(link: string, apiBaseUrl = ''): Promise<MapsLinkAnswer | null> {
  const answer = await post<MapsLinkAnswer>(MAPS_LINK_PATH, { link }, apiBaseUrl);
  return answer.ok ? answer.data : null;
}

/** The services an answer names, once each, in the order the parts were answered. */
export function answerSources(answer: MapsLinkAnswer): MapsLinkSource[] {
  return [...new Set(Object.values(answer.sources).filter((source): source is MapsLinkSource => Boolean(source)))];
}

type Shown = { kind: 'idle' } | { kind: 'reading' } | { kind: 'notMaps' } | { kind: 'failed' } | { kind: 'answered'; answer: MapsLinkAnswer };

export interface MapsLinkPasteProps {
  /** Reads one link through the API; a host hands its own, so a suite can stand in for the server. */
  read: (link: string) => Promise<MapsLinkAnswer | null>;
  /** Called once per link read for a place; the host fills its fields from it. */
  onPlace: (answer: MapsLinkAnswer) => void;
  /** A host with a sentence of its own to add once a place is read: the event names what it picked. */
  note?: string | null;
  /**
   * The line said once a place is read; the forms that save a pin keep the default,
   * and the event's Place, which writes no pin itself, passes null to say nothing of a pin.
   */
  filledLine?: string | null;
  /** Called when the field is emptied, or a paste reads no place: the host forgets any pin an earlier paste gave. */
  onCleared?: () => void;
}

export function MapsLinkPaste({ read, onPlace, note = null, filledLine = t('mapsLink.read'), onCleared }: MapsLinkPasteProps) {
  const [text, setText] = useState('');
  const [shown, setShown] = useState<Shown>({ kind: 'idle' });
  // 🔨 Only the latest paste answers: a slow read of an earlier link never fills over a newer one.
  const latest = useRef('');

  const readLink = useCallback(
    async (value: string) => {
      latest.current = value;
      setShown({ kind: 'reading' });
      const answer = await read(value);
      if (latest.current !== value) return;
      if (!answer) {
        setShown({ kind: 'failed' });
        onCleared?.();
        return;
      }
      setShown({ kind: 'answered', answer });
      // 🔨 Only a place gives a pin; any other answer forgets the one an earlier paste gave.
      if (answer.outcome === 'place') onPlace(answer);
      else onCleared?.();
    },
    [onCleared, onPlace, read],
  );

  const change = (value: string) => {
    setText(value);
    const trimmed = value.trim();
    if (!trimmed) {
      latest.current = '';
      setShown({ kind: 'idle' });
      onCleared?.();
      return;
    }
    // A pasted Maps link is read at once; any other web address says so at once; typing waits for the blur.
    if (readMapsLink(trimmed).kind !== 'notMaps') void readLink(trimmed);
    else {
      latest.current = '';
      onCleared?.();
      setShown(/^https?:\/\//i.test(trimmed) ? { kind: 'notMaps' } : { kind: 'idle' });
    }
  };

  const blur = () => {
    if (text.trim() && readMapsLink(text).kind === 'notMaps') setShown({ kind: 'notMaps' });
  };

  const answer = shown.kind === 'answered' ? shown.answer : null;
  const lines: { tone: 'good' | 'error'; text: string }[] = [];
  if (shown.kind === 'reading') lines.push({ tone: 'good', text: t('mapsLink.reading') });
  if (shown.kind === 'notMaps' || answer?.outcome === 'notMaps') lines.push({ tone: 'error', text: t('mapsLink.notMaps') });
  if (shown.kind === 'failed') lines.push({ tone: 'error', text: t('mapsLink.failed') });
  // One line for a link with no point: the short link that would not open, else the link that names none.
  if (answer?.outcome === 'noPoint') lines.push({ tone: 'error', text: t(answer.unread.includes('expansion') ? MAPS_LINK_UNREAD_KEYS.expansion : 'mapsLink.noPoint') });
  if (answer?.outcome === 'place') {
    if (filledLine) lines.push({ tone: 'good', text: filledLine });
    for (const source of answerSources(answer)) lines.push({ tone: 'good', text: t(MAPS_LINK_SOURCE_KEYS[source]) });
    if (note) lines.push({ tone: 'good', text: note });
  }
  if (answer?.outcome === 'place') for (const unread of answer.unread) lines.push({ tone: 'error', text: t(MAPS_LINK_UNREAD_KEYS[unread]) });

  return (
    <div data-maps-link={shown.kind === 'answered' ? shown.answer.outcome : shown.kind}>
      <TextInput size={Size.Large} label={t('mapsLink.label')} value={text} onChange={change} onBlur={blur} type="url" placeholder={t('mapsLink.placeholder')} />
      <p className={styles.hint}>{t('mapsLink.hint')}</p>
      {lines.map((line) => (
        <p key={line.text} className={line.tone === 'error' ? styles.verifyFailed : styles.hint} role="status" data-maps-link-line={line.tone}>
          {line.text}
        </p>
      ))}
    </div>
  );
}
